import { useState, useEffect, useCallback, useRef } from 'react';
import {
  carregarExpediente, carregarColegas, abrirPeriodo, fecharPeriodo,
  restaurarPeriodoAberto, montarAvisoRetorno, dentroDoExpediente,
  INATIVIDADE_LIMITE_MIN, TIPOS,
} from './atividadeApi';
import { operadorEstaAtendendo, ultimaAlteracaoDoOperador } from './mysuiteApi';
import { proximaAncora, minutosSemAtender } from './ociosidade';
import { ehDiaUtil, dataLocalISO } from './datas';
import {
  pausaLembra, lembretesDevidos, minutosNaPausa, textoDoLembrete,
} from './lembretePausa';
import { garantirPermissao, notificar } from './notificacaoSistema';

// ─────────────────────────────────────────────────────────────────────────────
// Controle de atividade — a orquestração.
//
// O serviço (atividadeApi) fala com o banco; este hook cuida do ciclo: carrega
// o expediente no login, restaura o período em aberto, troca de status, vigia
// a ociosidade e reativa quando o operador volta a trabalhar.
//
// Ficam de fora, e nem veem a pílula:
//   · quem não é do SUPORTE — a régua daqui é o atendimento de ticket, e fora
//     do suporte não há ticket para medir (ver `habilitado`, vindo das
//     permissões em services/permissoes.js);
//   · quem não tem jornada cadastrada em orion.operador (a diretoria).
//
// ─────────────────────────────────────────────────────────────────────────────
// O QUE É OCIOSIDADE, AQUI
//
// Só uma coisa: NÃO ESTAR ATENDENDO. Não é "não mexeu no mouse", não é "não
// abriu o Orion". São dois sinais do MySuite, nesta ordem:
//
//   1. tem ticket em atendimento (status 36)?  → está ativo, e ponto. O
//      cronômetro nem começa. A pergunta vai a DUAS fontes do MySuite e basta
//      uma responder que sim: elas divergem entre si, cada uma para um lado
//      (ver `operadorEstaAtendendo`, em mysuiteApi.js);
//   2. não tem? → o ócio corre desde a ÚLTIMA ALTERAÇÃO que ele fez em algum
//      ticket hoje. Não desde agora: se ele encerrou o último atendimento às
//      10h e são 11h, ele está sem atender há uma hora, e é isso que conta.
//      Esse instante — a âncora — só ANDA PARA A FRENTE, porque o MySuite
//      responde com atraso e uma resposta velha não pode envelhecer o
//      cronômetro (ver services/ociosidade.js).
//
// Do intervalo saem 5 minutos de desconto: a alteração leva um tempo até
// aparecer na API, e sem essa folga o operador apareceria ocioso enquanto o
// banco ainda está sendo alimentado.
//
// VOLTAR PARA ATIVO. Quem tira o operador da ociosidade é o trabalho, não o
// clique: assim que surge uma interação nova no MySuite, o status volta a Ativo
// sozinho. O botão manual de Ativo só existe para sair de uma pausa que o
// próprio operador declarou (almoço, café, apoio, fim de expediente) — não
// serve para apagar uma ociosidade apurada.
// ─────────────────────────────────────────────────────────────────────────────

/** De quanto em quanto tempo o monitor confere a ociosidade e os horários. */
const INTERVALO_MONITOR_MS = 60 * 1000;

/** Com quantos minutos de antecedência o lembrete avisa. */
const LEMBRETE_ANTECEDENCIA_MIN = 10;

/**
 * Por quanto tempo um lembrete de horário ainda vale a pena depois da hora.
 *
 * Era 2 minutos — a largura de um tique e meio. No app funcionava; no navegador
 * não: a aba em segundo plano tem os temporizadores agrupados pelo Chrome, o
 * tique pula de 11:59 para 12:02 e a janela inteira passa no vão. O aviso do
 * fim do almoço simplesmente nunca saía.
 *
 * Vinte minutos é largo o bastante para sobreviver a esse agrupamento e curto o
 * bastante para não ressuscitar o aviso do almoço às cinco da tarde, quando ele
 * já não quer dizer nada. Quem volta à aba depois disso é atendido pela
 * conferência imediata do `visibilitychange`, logo abaixo.
 */
const JANELA_LEMBRETE_MIN = 20;

/** Margem depois do fim da jornada antes de fechar o período sozinho. */
const MARGEM_FIM_JORNADA_MIN = 30;

/** De quanto em quanto tempo a fila do MySuite é consultada. */
const INTERVALO_CONSULTA_MS = 7 * 60 * 1000;

/** Quantos minutos antes do limite vale uma consulta extra, fora do ciclo. */
const PRECHECAGEM_MIN = 2;

/** Enquanto continuar sem atendimento, de quanto em quanto tempo reavisar. */
const REAVISO_MIN = 10;

/**
 * Desconto sobre o tempo desde a última alteração no MySuite.
 *
 * A alteração não aparece na API no instante em que acontece — há a gravação e
 * a alimentação do banco no meio. Sem esta folga, quem acabou de encerrar um
 * ticket já entraria no cronômetro com alguns minutos de atraso embutidos.
 */
const FOLGA_ALIMENTACAO_MIN = 5;

export function useAtividade({ operador, avisar, habilitado = true }) {
  const [expediente, setExpediente] = useState(null);
  const [naoCadastrado, setNaoCadastrado] = useState(false);
  const [periodo, setPeriodo] = useState(null);
  const [colegas, setColegas] = useState([]);
  // Minutos sem atendimento quando o alerta disparou; null = sem alerta.
  const [alertaOciosidade, setAlertaOciosidade] = useState(null);
  // Pausa declarada que passou do tempo: { tipo, rotulo, minutos }. null = sem
  // alerta. Ver o monitor de pausa, mais abaixo.
  const [alertaPausa, setAlertaPausa] = useState(null);

  // Refs para o monitor: ele roda num intervalo e precisa do valor do momento,
  // não do valor capturado quando o intervalo foi criado.
  const expedienteRef = useRef(null);
  const periodoRef = useRef(null);
  const gravandoRef = useRef(false);
  // Estado do monitor de ociosidade.
  const emAtendimentoRef = useRef(true);      // otimista: só marca ócio com certeza
  const ultimaConsultaRef = useRef(0);
  // Instante (ms) da última alteração do operador em algum ticket hoje. É daqui
  // que o cronômetro de ociosidade parte — e é a mudança deste valor que
  // devolve o operador para Ativo sozinho.
  const ultimaInteracaoRef = useRef(null);
  const prechecagemFeitaRef = useRef(false);
  const ultimoAvisoRef = useRef(0);
  // Escrevendo o atendimento (fases 2 e 3 do Helpdesk): conta como atender.
  const atendendoRef = useRef(false);
  // Lembretes já disparados hoje, para não repetir. A chave leva a data:
  // vira o dia e o conjunto deixa de casar sozinho.
  const lembretesRef = useRef(new Set());
  // Quantos lembretes de pausa já foram entregues, por período aberto:
  // { [chaveDoPeriodo]: quantidade }. O monitor compara este número com o que
  // o tempo decorrido exige — a diferença é o que falta avisar.
  const lembretesPausaRef = useRef({});

  useEffect(() => { expedienteRef.current = expediente; }, [expediente]);
  useEffect(() => { periodoRef.current = periodo; }, [periodo]);

  // ── Carga no login ────────────────────────────────────────────────────────
  useEffect(() => {
    if (!habilitado || !operador?.email) {
      setExpediente(null); setPeriodo(null); setNaoCadastrado(false);
      return undefined;
    }

    let vivo = true;
    (async () => {
      const { expediente: exp, naoCadastrado: semCadastro } = await carregarExpediente(operador.email);
      if (!vivo) return;
      setExpediente(exp);
      setNaoCadastrado(semCadastro);
      if (!exp) return;

      const [aberto, lista] = await Promise.all([
        restaurarPeriodoAberto(exp),
        carregarColegas(operador.email),
      ]);
      if (!vivo) return;
      setPeriodo(aberto);
      setColegas(lista);
    })();

    return () => { vivo = false; };
  }, [operador, habilitado]);

  // ── Troca de status ───────────────────────────────────────────────────────
  // Ponto único: fecha o período anterior e abre o novo.
  const definir = useCallback(async (status, tipo, opts = {}) => {
    const exp = expedienteRef.current;
    if (!exp || gravandoRef.current) return;

    // Já está exatamente neste estado: não cria período duplicado. A observação
    // entra na comparação por causa do tipo "outro" — trocar só a justificativa
    // é mudança de estado e precisa virar um período novo.
    const atual = periodoRef.current;
    if (atual
      && atual.status === status
      && (atual.tipo || null) === (tipo || null)
      && (atual.apoioCodigo || null) === (opts.apoioCodigo || null)
      && (atual.observacao || null) === (opts.observacao || null)) return;

    // Marcar uma pausa é o momento de pedir a permissão de notificação: o
    // navegador só aceita o pedido se ele nascer de um clique, e é justamente
    // aqui que ela passa a fazer diferença — o operador está saindo da frente
    // da tela, onde toast e modal não alcançam. Quem já respondeu (sim ou não)
    // não é perguntado de novo; ver services/notificacaoSistema.js.
    if (status === 'inativo' && opts.origem !== 'automatico' && pausaLembra(tipo)) {
      garantirPermissao();
    }

    gravandoRef.current = true;
    try {
      const agora = new Date();
      const resumo = await fecharPeriodo(exp, atual, agora);
      const novo = await abrirPeriodo(exp, operador, status, tipo, opts, agora);
      setPeriodo(novo);

      // Voltou a Ativo depois de uma pausa com tempo previsto: mostra o balanço.
      // Vale para a volta manual e para a automática (quando o operador mexe no
      // Orion e ele reativa sozinho).
      if (status === 'ativo') {
        const aviso = montarAvisoRetorno(exp, resumo);
        if (aviso) avisar(aviso.corpo, { tipo: aviso.tipo, titulo: aviso.titulo, duracao: 12000 });
      }
    } finally {
      gravandoRef.current = false;
    }
  }, [operador, avisar]);

  /**
   * Entrar na fase 2 ou 3 do Helpdesk é atender: o ticket ainda pode não estar
   * em status 36 no MySuite, mas o atendimento já está sendo escrito.
   *
   * Reativa quem estava em ociosidade AUTOMÁTICA. Almoço, café e apoio são
   * decisões do operador e só ele as desfaz.
   */
  const registrarAtendimento = useCallback((atendendo = true) => {
    atendendoRef.current = !!atendendo;
    if (!atendendo) return;
    // Escrever o atendimento é trabalho: vale como interação de agora, mesmo
    // que o MySuite só registre a alteração quando o texto for enviado.
    ultimaInteracaoRef.current = Date.now();
    const atual = periodoRef.current;
    if (atual && atual.status === 'inativo' && atual.tipo === 'ociosidade') {
      definir('ativo', null, { origem: 'automatico' });
    }
  }, [definir]);

  // ── Monitor de ociosidade ─────────────────────────────────────────────────
  //
  // Ver o cabeçalho do arquivo para a regra. Em resumo: ticket em atendimento
  // manda; sem ele, o ócio corre desde a última alteração no MySuite, com 5
  // minutos de folga.
  //
  // A consulta acontece a cada 7 minutos (são duas chamadas de rede, não cabem
  // a cada minuto) e uma vez a mais faltando 2 minutos para o limite, para a
  // decisão usar dados frescos.
  //
  // Em falha de rede, o estado anterior é MANTIDO. Nunca marcar ninguém de
  // ocioso porque a consulta não respondeu.
  useEffect(() => {
    if (!habilitado || !expediente || !operador?.codigo) return undefined;

    let vivo = true;

    const consultar = async () => {
      // As duas perguntas de uma vez: "está atendendo?" e "quando foi a última
      // vez que mexeu num ticket?". Em paralelo — uma não depende da outra.
      //
      // A primeira consulta DUAS fontes do MySuite e aceita qualquer uma delas
      // (ver `operadorEstaAtendendo`): as duas divergem, cada uma para um lado, e
      // acreditar em uma só marcava ocioso quem estava atendendo.
      const [atendendo, ultima] = await Promise.all([
        operadorEstaAtendendo(operador.codigo),
        ultimaAlteracaoDoOperador(operador.codigo),
      ]);
      if (!vivo) return;

      ultimaConsultaRef.current = Date.now();

      // Interação NOVA desde a consulta anterior: o operador voltou a trabalhar.
      // É isto que tira alguém da ociosidade — o trabalho, não o clique.
      //
      // A âncora só AVANÇA. O MySuite responde com atraso, e sobrescrever com o
      // que ele diz fazia o cronômetro andar para trás logo depois de um
      // atendimento concluído — ver o cabeçalho de services/ociosidade.js.
      const { ancora, houveInteracaoNova } = proximaAncora(
        ultimaInteracaoRef.current,
        ultima ? ultima.getTime() : null,
      );
      ultimaInteracaoRef.current = ancora;

      // null = não deu para saber. Mantém o que já sabia.
      if (atendendo !== null) emAtendimentoRef.current = atendendo;

      if (atendendo === true || houveInteracaoNova) {
        // Voltou a atender: zera o episódio e reativa quem estava ocioso por
        // decisão do monitor. Almoço, café e apoio são do operador e só ele
        // os desfaz.
        prechecagemFeitaRef.current = false;
        const atual = periodoRef.current;
        if (atual && atual.status === 'inativo' && atual.tipo === 'ociosidade') {
          definir('ativo', null, { origem: 'automatico' });
        }
      }
    };

    consultar();

    const id = setInterval(async () => {
      const exp = expedienteRef.current;
      const atual = periodoRef.current;
      if (!exp) return;

      // Fora da jornada, no almoço, em fim de semana ou feriado: não é ócio.
      if (!ehDiaUtil(new Date()) || !dentroDoExpediente(exp)) return;

      // Orion ficou aberto de um dia para o outro: o período em memória é de
      // ontem. Fecha no fim da jornada daquele dia (mesma regra do login) e
      // deixa o próximo tique abrir o "Ativo" de hoje.
      if (atual && atual.inicio && dataLocalISO(new Date(atual.inicio)) !== dataLocalISO(new Date())) {
        if (gravandoRef.current) return;
        gravandoRef.current = true;
        try {
          const restaurado = await restaurarPeriodoAberto(exp);
          periodoRef.current = restaurado;
          setPeriodo(restaurado);
        } finally {
          gravandoRef.current = false;
        }
        return;
      }

      // Dentro da jornada e sem nenhum período aberto: o operador está com o
      // Orion aberto, então está Ativo. Sem isto, quem abre o Orion e trabalha
      // sem pausa não gera nenhuma linha no dia e o painel o mostra como
      // "Orion fechado". Também cobre a gravação que falhou (banco fora do ar):
      // tenta de novo a cada minuto até conseguir.
      if (!atual) {
        await definir('ativo', null, { origem: 'automatico' });
        return;
      }

      // Inatividade já declarada pelo operador suspende o monitor. A exceção é
      // a própria ociosidade: se ela suspendesse, os re-avisos parariam.
      if (atual && atual.status === 'inativo' && atual.tipo !== 'ociosidade') return;

      // Escrevendo o atendimento (fases 2 e 3): está atendendo, mesmo que o
      // MySuite ainda não mostre o ticket em 36. Sem isto o contador corria
      // durante o atendimento e disparava logo depois de finalizar.
      if (atendendoRef.current) {
        ultimaInteracaoRef.current = Date.now();
        return;
      }

      // Consulta periódica.
      if (Date.now() - ultimaConsultaRef.current >= INTERVALO_CONSULTA_MS) {
        await consultar();
        if (!vivo) return;
      }

      if (emAtendimentoRef.current) return;

      // Sem ticket em atendimento: o cronômetro corre desde a ÚLTIMA ALTERAÇÃO
      // no MySuite, e não desde agora. Quem encerrou o último atendimento às
      // 10h já está sem atender há uma hora às 11h — o Orion não pode fingir
      // que o relógio começou quando ele abriu o app.
      //
      // Sem nenhuma interação hoje ("não sei"), o monitor não opina: marcar
      // ociosidade desde a meia-noite seria inventar um número.
      const minutosSemTicket = minutosSemAtender(
        ultimaInteracaoRef.current,
        Date.now(),
        FOLGA_ALIMENTACAO_MIN,
      );
      if (minutosSemTicket === null) return;

      // Pré-checagem: faltando 2 min, confere fora do ciclo. Se pegou um ticket
      // nesse meio-tempo, o episódio é cancelado sem nunca ter avisado.
      if (!prechecagemFeitaRef.current
        && minutosSemTicket >= INATIVIDADE_LIMITE_MIN - PRECHECAGEM_MIN) {
        prechecagemFeitaRef.current = true;
        await consultar();
        if (!vivo || emAtendimentoRef.current) return;
      }

      if (minutosSemTicket < INATIVIDADE_LIMITE_MIN) return;

      // Primeiro aviso do episódio: é o único que registra a ociosidade.
      const jaMarcado = atual && atual.status === 'inativo' && atual.tipo === 'ociosidade';
      const desdeUltimoAviso = (Date.now() - (ultimoAvisoRef.current || 0)) / 60000;
      if (jaMarcado && desdeUltimoAviso < REAVISO_MIN) return;

      ultimoAvisoRef.current = Date.now();
      if (!jaMarcado) definir('inativo', 'ociosidade', { origem: 'automatico' });
      setAlertaOciosidade(Math.round(minutosSemTicket));
    }, INTERVALO_MONITOR_MS);

    return () => { vivo = false; clearInterval(id); };
  }, [expediente, operador, definir, habilitado]);

  // ── Monitor de pausa declarada ────────────────────────────────────────────
  //
  // O operador marcou Almoço, Café, Apoio ou Outro e não desmarcou. Este
  // monitor o lembra: o almoço depois de uma hora, as demais pausas aos 15
  // minutos, e daí em diante de 15 em 15 (ver services/lembretePausa.js).
  //
  // É diferente dos lembretes de horário logo abaixo. Aqueles olham o relógio
  // do EXPEDIENTE cadastrado — "seu almoço termina às 13h". Este olha a PAUSA
  // que o operador de fato abriu, e conta a partir dela. Quem sai para almoçar
  // às 12h40 não é ajudado por um aviso preso ao horário de 12h.
  //
  // O lembrete sai por três canais ao mesmo tempo, e cada um cobre o que os
  // outros não alcançam:
  //   · notificação do sistema — a única que chega a quem está longe da tela,
  //     com o navegador minimizado. É o caso do almoço, e foi o que faltou;
  //   · modal — impossível de ignorar quando ele volta à janela;
  //   · toast — para quem está com o Orion à vista e não precisa de mais.
  useEffect(() => {
    const conferir = () => {
      const atual = periodoRef.current;

      // Fora de uma pausa que lembra: some com o alerta. Voltar para Ativo é
      // exatamente a resposta que ele pedia — insistir depois disso seria
      // cobrar algo que já foi feito.
      if (!atual || atual.status !== 'inativo' || !pausaLembra(atual.tipo)) {
        setAlertaPausa(null);
        return;
      }

      // A chave é do PERÍODO, não do tipo: duas pausas seguidas do mesmo tipo
      // são dois episódios, e a contagem de uma não pode calar a outra.
      const chave = atual.id
        ?? (atual.inicio ? new Date(atual.inicio).getTime() : 'sem-id');

      const minutos = minutosNaPausa(atual.inicio);
      if (minutos === null) return;

      // O coração da coisa: quantos lembretes o tempo decorrido já exigia,
      // contra quantos foram entregues. Se a aba dormiu quarenta minutos, a
      // diferença aparece aqui e o lembrete sai — atrasado, mas sai. Sai UM,
      // e não a fila inteira: o que interessa é o estado de agora.
      const devidos = lembretesDevidos(atual.tipo, minutos);
      const dados = lembretesPausaRef.current[chave] || 0;
      if (devidos <= dados) return;

      lembretesPausaRef.current = { ...lembretesPausaRef.current, [chave]: devidos };

      const rotulo = (TIPOS[atual.tipo] || {}).rotulo || 'Pausa';
      const { titulo, corpo } = textoDoLembrete(atual.tipo, minutos, rotulo);

      // A etiqueta faz a notificação nova substituir a anterior desta mesma
      // pausa: quem volta às 14h encontra um aviso, não seis empilhados.
      notificar(titulo, corpo, { tag: `orion-pausa-${chave}`, insistir: true });
      avisar(corpo, { tipo: 'info', titulo, duracao: 16000 });
      setAlertaPausa({ tipo: atual.tipo, rotulo, minutos });
    };

    conferir();
    const id = setInterval(conferir, INTERVALO_MONITOR_MS);

    // Voltar para a aba confere na hora. O intervalo acima pode ter passado o
    // almoço inteiro engasgado pelo navegador; este é o momento em que o
    // relógio volta a ser confiável.
    const aoVoltar = () => { if (!document.hidden) conferir(); };
    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('focus', aoVoltar);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', aoVoltar);
      window.removeEventListener('focus', aoVoltar);
    };
  }, [avisar, habilitado]);

  // ── Lembretes de horário ──────────────────────────────────────────────────
  // Almoço (ida e volta) e fim de expediente, cada um avisado 10 min antes e
  // na hora. As janelas de disparo existem porque o tique é de 1 min e pode
  // atrasar — muito, no navegador (ver JANELA_LEMBRETE_MIN); o conjunto de
  // disparados impede repetir o mesmo aviso.
  //
  // Estes olham o EXPEDIENTE cadastrado. Quem cobre a pausa que o operador
  // abriu de fato é o monitor logo acima.
  useEffect(() => {
    if (!expediente) return undefined;

    const conferir = () => {
      const exp = expedienteRef.current;
      const agora = new Date();
      if (!exp || !ehDiaUtil(agora)) return;

      const min = agora.getHours() * 60 + agora.getMinutes();
      const hoje = dataLocalISO(agora);
      // Duas janelas, e não uma, porque elas querem coisas diferentes.
      //
      // A de antecedência vale do "faltam 10 minutos" até a hora em ponto, e
      // MORRE ali: quem abre o Orion 12:25 não pode receber "faltam 10 minutos
      // para o almoço" — o almoço já começou, e o aviso viraria mentira.
      //
      // A da hora, essa sim, se estende — é a que precisa sobreviver ao
      // agrupamento de temporizadores do navegador (ver JANELA_LEMBRETE_MIN).
      // Em ambas, o conjunto de disparados garante que o aviso saia uma vez só.
      const naAntecedencia = alvo => min >= alvo - LEMBRETE_ANTECEDENCIA_MIN && min < alvo;
      const naHora = alvo => min >= alvo && min < alvo + JANELA_LEMBRETE_MIN;

      const eventos = [
        exp.almocoIni != null && {
          nome: 'almoco_ini', alvo: exp.almocoIni,
          tituloAviso: 'Almoço se aproximando',
          msgAviso: `Faltam ${LEMBRETE_ANTECEDENCIA_MIN} minutos para o seu horário de almoço. Lembre-se de alterar seu status antes de sair.`,
          tituloHora: 'Hora do almoço',
          msgHora: 'Seu horário de almoço começou. Marque o status como Almoço para registrar a saída.',
        },
        exp.almocoFim != null && {
          nome: 'almoco_fim', alvo: exp.almocoFim,
          tituloAviso: 'Volta do almoço',
          msgAviso: `Faltam ${LEMBRETE_ANTECEDENCIA_MIN} minutos para o fim do seu almoço.`,
          tituloHora: 'Fim do almoço',
          msgHora: 'Seu almoço terminou. Volte o status para Ativo — passar do horário gera atraso.',
        },
        exp.fim != null && {
          nome: 'jornada_fim', alvo: exp.fim,
          tituloAviso: 'Fim de expediente se aproximando',
          msgAviso: `Faltam ${LEMBRETE_ANTECEDENCIA_MIN} minutos para o fim do seu expediente.`,
          tituloHora: 'Fim do expediente',
          msgHora: 'Seu expediente terminou. Marque “Fora do expediente” para registrar o horário de saída.',
        },
      ].filter(Boolean);

      for (const ev of eventos) {
        const chaveAviso = `${hoje}|${ev.nome}|aviso`;
        if (naAntecedencia(ev.alvo) && !lembretesRef.current.has(chaveAviso)) {
          lembretesRef.current.add(chaveAviso);
          avisar(ev.msgAviso, { tipo: 'info', titulo: ev.tituloAviso, duracao: 14000 });
          notificar(ev.tituloAviso, ev.msgAviso, { tag: `orion-${ev.nome}-aviso` });
          return;   // um lembrete por vez
        }
        const chaveHora = `${hoje}|${ev.nome}|hora`;
        if (naHora(ev.alvo) && !lembretesRef.current.has(chaveHora)) {
          lembretesRef.current.add(chaveHora);
          avisar(ev.msgHora, { tipo: 'info', titulo: ev.tituloHora, duracao: 16000 });
          notificar(ev.tituloHora, ev.msgHora, { tag: `orion-${ev.nome}-hora` });
          return;
        }
      }

      // Rede de segurança: 30 min depois do fim da jornada, fecha o período
      // que ficou aberto. Cobre quem foi embora e deixou o Orion ligado. A
      // margem evita brigar com o lembrete de fim de expediente, que o
      // operador ainda pode responder.
      const atual = periodoRef.current;
      if (atual && exp.fim != null && min > exp.fim + MARGEM_FIM_JORNADA_MIN
        && atual.tipo !== 'fora_expediente') {
        definir('inativo', 'fora_expediente', { origem: 'automatico' });
      }
    };

    const id = setInterval(conferir, INTERVALO_MONITOR_MS);

    // Mesma razão do monitor de pausa: a aba que estava em segundo plano pode
    // ter atravessado a janela inteira sem um tique. Quando ela volta, a
    // conferência acontece de imediato.
    const aoVoltar = () => { if (!document.hidden) conferir(); };
    document.addEventListener('visibilitychange', aoVoltar);
    window.addEventListener('focus', aoVoltar);

    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', aoVoltar);
      window.removeEventListener('focus', aoVoltar);
    };
  }, [expediente, definir, avisar]);

  /**
   * O operador pode se marcar como Ativo agora?
   *
   * Sim quando ele está numa pausa que ELE declarou — almoço, café, apoio, fim
   * de expediente, outro. Voltar dessas é decisão dele, e o botão é a única
   * forma de fazê-lo.
   *
   * NÃO quando o período atual é `ociosidade`. Essa não foi declarada: foi
   * APURADA a partir do MySuite. Deixar o botão desfazê-la transformaria a
   * medição em autodeclaração — bastaria clicar em Ativo para o tempo parado
   * sumir do relatório. Quem tira alguém da ociosidade é a interação seguinte
   * num ticket, e o monitor reativa sozinho quando ela aparece.
   */
  const podeVoltarAtivo = !(periodo && periodo.status === 'inativo' && periodo.tipo === 'ociosidade');

  return {
    /** true quando este operador está sob controle de atividade. */
    ativo: !!expediente,
    naoCadastrado,
    expediente,
    periodo,
    colegas,
    definir,
    podeVoltarAtivo,
    registrarAtendimento,
    /** Minutos sem atendimento quando o alerta disparou; null = sem alerta. */
    alertaOciosidade,
    /** Chamado pelo botão "Entendi" do alerta. */
    fecharAlertaOciosidade: () => setAlertaOciosidade(null),
    /** Pausa passada do tempo: { tipo, rotulo, minutos }; null = sem alerta. */
    alertaPausa,
    /** Chamado pelo botão "Continuar na pausa". */
    fecharAlertaPausa: () => setAlertaPausa(null),
  };
}
